//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PluginCard from './PluginCards';
import AllPuginPage from './AllPuginPage';
import { ReactComponent as LeftArrow } from '../../icons/PluginLeft.svg';
import { ReactComponent as RightArrow } from '../../icons/PluginRight.svg';
import { Close } from '@carbon/icons-react';
import mockData from '../../mockData.json';

const CARDS_PER_PAGE = 4;

interface PluginData {
  appName: string;
  title: string;
  content: string;
  createdAt: string;
  link: string;
  plugintype: string;
  provider: string;
  imageUrl: string;
  nameLabel?: string;
}

// Plugin Detail Panel component
export function PluginDetailPanel({
  plugin,
  onClose,
}: {
  plugin: PluginData;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  if (!plugin) return null;

  const tagColor = (() => {
    const category = (plugin.appName || plugin.nameLabel || '').toLowerCase();
    switch (category) {
      case 'measure':
        return 'bg-[#24A148] text-white';
      case 'deploy':
        return 'bg-[#0F62FE] text-white';
      case 'plan':
        return 'bg-[#8A3FFC] text-white';
      case 'build':
        return 'bg-[#F1C21B] text-gray-900';
      default:
        return 'bg-gray-500 text-white';
    }
  })();

  return (
    <aside
      aria-labelledby="plugin-detail-title"
      className="flex h-full w-[657px] flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-[#262626]"
    >
      {/* Header */}
      <div
        id="id-plugin-detail-header"
        data-testid="plugin-detail-header"
        className="flex items-center justify-between border-b border-gray-200 bg-[#a2a9b0]/20 px-4 py-3 dark:border-gray-700"
      >
        <h2
          className="text-[16px] font-semibold text-gray-700 dark:text-gray-300"
          id="plugin-detail-title"
        >
          {t('pluginDetails')}
        </h2>
        <button
          onClick={onClose}
          aria-label="Close plugin details panel"
          className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          <Close
            size={16}
            className="text-gray-600 dark:text-gray-400"
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Content */}
      <div
        id="id-plugin-detail-content"
        data-testid="plugin-detail-content"
        className="flex-1 overflow-y-auto p-6"
      >
        {/* Logo and Install Button */}
        <div
          id="id-plugin-detail-logo-section"
          data-testid="plugin-detail-logo-section"
          className="mb-4 flex items-start justify-between"
        >
          <div
            id="id-plugin-detail-logo-wrapper"
            data-testid="plugin-detail-logo-wrapper"
          >
            <img
              src={plugin.imageUrl}
              alt={`${plugin.title} icon`}
              className="h-8 object-contain"
            />
          </div>
          <button className="rounded bg-[#3c91ff] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#2975dd] focus:outline-none focus:ring-2 focus:ring-[#3c91ff] focus:ring-offset-2 dark:bg-[#3c91ff] dark:hover:bg-[#5aa3ff] dark:focus:ring-offset-[#262626]">
            {t('installPlugin')}
          </button>
        </div>

        {/* Tag */}
        <div
          id="id-plugin-detail-tag"
          data-testid="plugin-detail-tag"
          className="mb-4"
        >
          <span
            id="id-plugin-detail-tag-badge"
            data-testid="plugin-detail-tag-badge"
            className={`rounded px-2 py-0.5 text-xs font-medium ${tagColor}`}
          >
            {t(plugin.appName || plugin.nameLabel || '')}
          </span>
        </div>

        {/* Plugin Name */}
        <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
          {plugin.title}
        </h3>

        {/* Version */}
        <div
          id="id-plugin-detail-version"
          data-testid="plugin-detail-version"
          className="mb-6"
        >
          <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('version')}
          </h4>
          <p className="text-sm text-gray-800 dark:text-gray-200">1.0.0</p>
        </div>

        {/* Overview */}
        <div
          id="id-plugin-detail-overview"
          data-testid="plugin-detail-overview"
          className="mb-6"
        >
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('overview')}
          </h4>
          <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            {plugin.content}
          </p>
        </div>

        {/* Requirements */}
        <div
          id="id-plugin-detail-requirements"
          data-testid="plugin-detail-requirements"
          className="mb-6"
        >
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('requirements')}
          </h4>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span
                id="id-plugin-detail-bullet-1"
                data-testid="plugin-detail-bullet-1"
                className="mt-0.5 text-[#24A148]"
              >
                •
              </span>
              DevOps Loop v1.0+
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span
                id="id-plugin-detail-bullet-2"
                data-testid="plugin-detail-bullet-2"
                className="mt-0.5 text-[#24A148]"
              >
                •
              </span>
              {plugin.appName} credentials
            </li>
          </ul>
        </div>
      </div>
    </aside>
  );
}

function PluginPage() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [shuffledCards, setShuffledCards] = useState<PluginData[]>([]);
  const [selectedPlugin, setSelectedPlugin] = useState<PluginData | null>(null);

  useEffect(() => {
    const shuffleArray = (array: PluginData[]): PluginData[] => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(
          (crypto.getRandomValues(new Uint32Array(1))[0] / (0xffffffff + 1)) *
            (i + 1),
        );
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    };

    const shuffled = shuffleArray([...mockData]).slice(0, 12);
    queueMicrotask(() => setShuffledCards(shuffled));
  }, []);

  const cardsData = shuffledCards.map((item) => ({
    title: item.title,
    text: item.content,
    imageUrl: item.imageUrl,
    buttonLink: item.link,
    nameLabel: item.appName,
    content: item.content,
    createdAt: item.createdAt,
    link: item.link,
    appName: item.appName,
    plugintype: item.plugintype,
    provider: item.provider,
  }));

  const totalPages = Math.ceil(cardsData.length / CARDS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const displayedCards = cardsData.slice(
    (currentPage - 1) * CARDS_PER_PAGE,
    currentPage * CARDS_PER_PAGE,
  );

  const handleViewDetails = (plugin: PluginData) => {
    setSelectedPlugin(plugin);
  };

  const handleClosePanel = () => {
    setSelectedPlugin(null);
  };

  return (
    <div
      id="plugin-container"
      data-testid="plugin-container"
      className="flex size-full flex-col bg-[#f4f4f4] dark:bg-[#161616]"
    >
      {/* Content Area - Grey background with white card */}
      <div
        id="id-plugin-content-area"
        data-testid="plugin-content-area"
        className="flex-1 overflow-auto p-6"
      >
        <div
          id="id-plugin-content-card"
          data-testid="plugin-content-card"
          className="mx-auto rounded-lg bg-white p-6 shadow-sm dark:bg-[#262626]"
        >
          {/* Featured Plugins Section */}
          <div
            id="id-plugin-featured-section"
            data-testid="plugin-featured-section"
            className="mb-8"
          >
            <div
              id="id-plugin-featured-header"
              data-testid="plugin-featured-header"
              className="mb-4 flex items-center justify-between"
            >
              <h2 className="text-base font-medium text-[#4a4a56] dark:text-[#c8c8dd]">
                {t('featuredPlugins')}
              </h2>
              {cardsData.length > CARDS_PER_PAGE && (
                <div
                  id="id-plugin-featured-pagination"
                  data-testid="plugin-featured-pagination"
                  className="flex items-center gap-2"
                >
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-[#262626] dark:hover:bg-gray-700`}
                    title="Previous"
                    aria-label={t('prevPluginLabel')}
                  >
                    <LeftArrow />
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`flex size-8 items-center justify-center rounded-full border border-gray-300 bg-white transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-[#262626] dark:hover:bg-gray-700`}
                    title="Next"
                    aria-label={t('nextPluginLabel')}
                  >
                    <RightArrow />
                  </button>
                </div>
              )}
            </div>
            <div
              id="id-plugin-featured-cards"
              data-testid="plugin-featured-cards"
              className="flex gap-4 overflow-x-auto pb-2"
            >
              {displayedCards.map((card, index) => (
                <PluginCard
                  key={index}
                  title={card.title}
                  text={card.text}
                  imageUrl={card.imageUrl}
                  buttonLink={card.buttonLink}
                  nameLabel={card.nameLabel}
                  appName={''}
                  content={card.content}
                  createdAt={card.createdAt}
                  link={card.link}
                  onViewDetails={() => handleViewDetails(card)}
                />
              ))}
            </div>
          </div>

          {/* All Plugins Section - inside the same white card */}
          <AllPuginPage onViewDetails={handleViewDetails} />
        </div>
      </div>

      {/* Plugin Detail Panel */}
      {selectedPlugin && (
        <>
          {/* Backdrop */}
          <div
            id="id-plugin-detail-backdrop"
            data-testid="plugin-detail-backdrop"
            role="button"
            tabIndex={0}
            className="fixed inset-0 z-40 bg-black/20"
            onClick={handleClosePanel}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleClosePanel();
            }}
          />
          {/* Panel */}
          <div
            id="id-plugin-detail-panel-wrapper"
            data-testid="plugin-detail-panel-wrapper"
            className="fixed inset-y-0 right-0 z-50"
          >
            <PluginDetailPanel
              plugin={selectedPlugin}
              onClose={handleClosePanel}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default PluginPage;
